import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { casesAPI, organizationsAPI, customFieldsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { format } from 'date-fns';
import { Loader2, ArrowLeft, CalendarIcon, User, Phone, Globe, Fingerprint, Briefcase, ChevronDown, ChevronRight, Crown } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import Sidebar from '../components/Sidebar';
import FormScanner from '../components/FormScanner';
import AddCustomFieldDialog from '../components/AddCustomFieldDialog';
import { CountyAutocomplete } from '../components/CountyAutocomplete';
import { US_STATE_OPTIONS } from '../lib/us-counties';
import { Badge } from '../components/ui/badge';

function Section({ title, icon: Icon, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card className="mb-4">
      <CardHeader className="pb-2 cursor-pointer" onClick={() => setOpen(!open)}>
        <CardTitle className="font-heading text-base flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-primary" />}
          {title}
          {open ? <ChevronDown className="w-4 h-4 ml-auto text-muted-foreground" /> : <ChevronRight className="w-4 h-4 ml-auto text-muted-foreground" />}
        </CardTitle>
      </CardHeader>
      {open && <CardContent>{children}</CardContent>}
    </Card>
  );
}

function Field({ label, field, value, onChange, type = 'text', options = [], required = false }) {
  if (type === 'select') {
    return (
      <div className="space-y-1">
        <Label className="font-body text-sm">{label}{required && <span className="text-error ml-1">*</span>}</Label>
        <Select value={value || ''} onValueChange={(v) => onChange(field, v)}>
          <SelectTrigger className="font-body text-sm h-9 bg-card" data-testid={`field-${field}`}><SelectValue placeholder={`Select...`} /></SelectTrigger>
          <SelectContent>{options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    );
  }
  if (type === 'date') {
    // Safely parse the date value, stripping ordinal suffixes (1st, 2nd, 3rd, 4th, etc.)
    let parsedDate = null;
    let displayText = '';
    if (value) {
      const cleaned = String(value).replace(/(\d+)(st|nd|rd|th)/g, '$1');
      const d = new Date(cleaned);
      if (!isNaN(d.getTime())) {
        parsedDate = d;
        try { displayText = format(d, 'PPP'); } catch { displayText = value; }
      } else {
        displayText = value;
      }
    }
    return (
      <div className="space-y-1">
        <Label className="font-body text-sm">{label}</Label>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className={cn("w-full justify-start text-left font-body text-sm h-9 bg-card", !value && "text-muted-foreground")} data-testid={`field-${field}`}>
              <CalendarIcon className="mr-2 h-3 w-3" />
              {value ? displayText : <span>Pick a date</span>}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar mode="single" selected={parsedDate || undefined} onSelect={(d) => onChange(field, d ? format(d, 'yyyy-MM-dd') : '')} initialFocus />
          </PopoverContent>
        </Popover>
      </div>
    );
  }
  if (type === 'textarea') {
    return (
      <div className="space-y-1 col-span-full">
        <Label className="font-body text-sm">{label}</Label>
        <Textarea value={value || ''} onChange={(e) => onChange(field, e.target.value)} className="font-body text-sm bg-card" rows={2} data-testid={`field-${field}`} />
      </div>
    );
  }
  return (
    <div className="space-y-1">
      <Label className="font-body text-sm">{label}{required && <span className="text-error ml-1">*</span>}</Label>
      <Input value={value || ''} onChange={(e) => onChange(field, e.target.value)} className="font-body text-sm h-9 bg-card" data-testid={`field-${field}`} />
    </div>
  );
}

export default function CaseFormPage() {
  const { isSuperAdmin, organizationId, hasFeature } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [customFields, setCustomFields] = useState([]);
  const [customValues, setCustomValues] = useState({});
  const [formData, setFormData] = useState({
    name: '', first_name: '', middle_name: '', last_name: '', maiden_name: '', title: '',
    sex: '', marital_status: '', dob: '', city_of_birth: '', state_of_birth: '',
    country_of_birth: '', citizenship: '', ssn: '', a_number: '', major_field_of_study: '', degree: '',
    email: '', day_phone: '', cell_phone: '', evening_phone: '', fax: '', telephone: '',
    address: '', mailing_address: '', state: '', county: '',
    country: '', type_of_services: '', date_of_service: '', date_of_arrival: '',
    uscis_receipt: '', receive_by: '', uscis_date: '', uscis_decision: '', biometrics_date: '',
    nonimmigrant_status: '', nonimmigrant_status_exp_date: '', nonimmigrant_status_max_exp_date: '', department: '',
    ethnicity: '', race: '', height: '', weight: '', eye_color: '', hair_color: '',
    employer_name: '', business_type: '', job_title: '', job_description: '',
    supervisor_name: '', hours_per_week: '', employer_email: '',
  });

  useEffect(() => { if (isSuperAdmin) fetchOrganizations(); }, [isSuperAdmin]);
  useEffect(() => { fetchCustomFields(); }, []);

  const fetchOrganizations = async () => {
    try {
      const response = await organizationsAPI.getAll();
      setOrganizations(response.data.filter(o => o.is_active));
    } catch { toast.error('Failed to load organizations'); }
  };

  const fetchCustomFields = async () => {
    try {
      const res = await customFieldsAPI.list();
      setCustomFields(res.data.filter(f => f.is_standard));
    } catch { /* silent */ }
  };

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));
  const handleCustomChange = (key, value) => setCustomValues(prev => ({ ...prev, [key]: value }));

  const handleScanData = (data) => {
    // Parse name into components (same logic as batch scanner)
    const parts = (data.name || '').trim().split(/\s+/);
    const firstName = data.first_name || parts[0] || '';
    const lastName = data.last_name || (parts.length > 1 ? parts[parts.length - 1] : '');
    const middleName = data.middle_name || (parts.length > 2 ? parts.slice(1, -1).join(' ') : '');

    const mapped = {
      name: data.name || [firstName, middleName, lastName].filter(Boolean).join(' '),
      first_name: firstName, middle_name: middleName, last_name: lastName,
    };

    // Map ALL scalar fields from extracted data
    const scalarFields = [
      'maiden_name', 'title', 'sex', 'marital_status', 'dob',
      'city_of_birth', 'state_of_birth', 'country_of_birth', 'citizenship',
      'ssn', 'a_number', 'major_field_of_study', 'degree',
      'email', 'telephone', 'day_phone', 'cell_phone', 'evening_phone', 'fax',
      'address', 'mailing_address', 'state', 'county', 'country',
      'date_of_arrival', 'uscis_receipt', 'nonimmigrant_status',
      'nonimmigrant_status_exp_date', 'biometrics_date',
      'ethnicity', 'race', 'height', 'weight', 'eye_color', 'hair_color',
      'employer_name', 'employer_address', 'business_type', 'job_title',
      'job_description', 'supervisor_name', 'hours_per_week', 'employer_email',
      'emergency_contact_name', 'emergency_contact_phone',
    ];
    for (const f of scalarFields) {
      if (data[f] && typeof data[f] === 'string') mapped[f] = data[f];
    }
    if (data.type_of_service) mapped.type_of_services = data.type_of_service;
    if (!mapped.country && data.country_of_birth) mapped.country = data.country_of_birth;

    // Map list fields
    const listFields = [
      'other_names', 'previous_addresses', 'previous_employment', 'education',
      'marriages', 'members', 'abroad_trips', 'passports', 'visas', 'i94s',
    ];
    for (const f of listFields) {
      if (Array.isArray(data[f]) && data[f].length > 0) mapped[f] = data[f];
    }

    setFormData(prev => ({ ...prev, ...mapped }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { toast.error('Case name is required'); return; }
    if (isSuperAdmin && !selectedOrgId) { toast.error('Please select an organization'); return; }
    setLoading(true);
    try {
      const orgId = isSuperAdmin ? selectedOrgId : organizationId;
      const payload = { ...formData };
      if (Object.keys(customValues).length > 0) payload.custom_fields = customValues;
      await casesAPI.create(payload, orgId);
      toast.success('Case created successfully');
      navigate('/cases');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to create case'); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />
      <main className="ml-0 md:ml-64 p-4 md:p-8 pt-16 md:pt-8">
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate('/cases')} className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground font-body text-sm mb-4 transition-colors" data-testid="back-to-cases">
            <ArrowLeft className="w-4 h-4" /> Back to Cases
          </Button>
          <h1 className="font-heading text-2xl font-bold text-foreground tracking-tight">Add New Case</h1>
          <p className="font-body text-muted-foreground">Enter the immigration case information below</p>
        </div>

        <div className="max-w-5xl">
          {/* AI Form Scanner */}
          {hasFeature('form_scanner') ? (
            <div className="mb-6"><FormScanner onDataExtracted={handleScanData} /></div>
          ) : (
            <Card className="mb-6 border-dashed opacity-60" data-testid="form-scanner-locked">
              <CardContent className="py-6 text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Crown className="w-5 h-5 text-amber-500" />
                  <Badge className="bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/20 hover:bg-amber-500/15">Premium Feature</Badge>
                </div>
                <p className="font-body text-sm text-muted-foreground">AI Form Scanner is a premium add-on. Contact your administrator to enable this feature.</p>
              </CardContent>
            </Card>
          )}

          {/* Org Selector for Super Admin */}
          {isSuperAdmin && (
            <Card className="mb-4">
              <CardContent className="pt-6">
                <Label className="font-body text-sm">Organization *</Label>
                <Select value={selectedOrgId} onValueChange={setSelectedOrgId}>
                  <SelectTrigger className="font-body mt-1" data-testid="org-selector">
                    <SelectValue placeholder="Select an organization" />
                  </SelectTrigger>
                  <SelectContent>
                    {organizations.map(org => <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>
          )}

          <form onSubmit={handleSubmit} data-testid="case-form">
            {/* Add Custom Field Button */}
            <div className="flex justify-end mb-4">
              <AddCustomFieldDialog
                organizationId={isSuperAdmin ? selectedOrgId : organizationId}
                onFieldAdded={fetchCustomFields}
              />
            </div>

            {/* Personal Info */}
            <Section title="Personal Information" icon={User}>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                <Field label="Case Name" field="name" value={formData.name} onChange={handleChange} required />
                <Field label="First Name" field="first_name" value={formData.first_name} onChange={handleChange} />
                <Field label="Middle Name" field="middle_name" value={formData.middle_name} onChange={handleChange} />
                <Field label="Last Name" field="last_name" value={formData.last_name} onChange={handleChange} />
                <Field label="Maiden Name" field="maiden_name" value={formData.maiden_name} onChange={handleChange} />
                <Field label="Title" field="title" value={formData.title} onChange={handleChange} type="select" options={['Mr.', 'Mrs.', 'Ms.', 'Dr.', 'Prof.']} />
                <Field label="Sex" field="sex" value={formData.sex} onChange={handleChange} type="select" options={['Male', 'Female', 'Other']} />
                <Field label="Marital Status" field="marital_status" value={formData.marital_status} onChange={handleChange} type="select" options={['Single', 'Married', 'Divorced', 'Widowed', 'Separated']} />
                <Field label="Date of Birth" field="dob" value={formData.dob} onChange={handleChange} type="date" />
                <Field label="City of Birth" field="city_of_birth" value={formData.city_of_birth} onChange={handleChange} />
                <Field label="State of Birth" field="state_of_birth" value={formData.state_of_birth} onChange={handleChange} />
                <Field label="Country of Birth" field="country_of_birth" value={formData.country_of_birth} onChange={handleChange} />
                <Field label="Citizenship" field="citizenship" value={formData.citizenship} onChange={handleChange} />
                <Field label="SSN" field="ssn" value={formData.ssn} onChange={handleChange} />
                <Field label="Alien Number" field="a_number" value={formData.a_number} onChange={handleChange} />
                <Field label="Major Field of Study" field="major_field_of_study" value={formData.major_field_of_study} onChange={handleChange} />
                <Field label="Degree" field="degree" value={formData.degree} onChange={handleChange} />
                {customFields.filter(f => f.group === 'personal').map(f => (
                  <Field key={f.id} label={f.field_name} field={f.field_key} value={customValues[f.field_key] || ''} onChange={(_, v) => handleCustomChange(f.field_key, v)} />
                ))}
              </div>
            </Section>

            {/* Contact & Address */}
            <Section title="Contact & Address" icon={Phone} defaultOpen={false}>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                <Field label="Email" field="email" value={formData.email} onChange={handleChange} />
                <Field label="Day Phone" field="day_phone" value={formData.day_phone} onChange={handleChange} />
                <Field label="Cell Phone" field="cell_phone" value={formData.cell_phone} onChange={handleChange} />
                <Field label="Evening Phone" field="evening_phone" value={formData.evening_phone} onChange={handleChange} />
                <Field label="Fax" field="fax" value={formData.fax} onChange={handleChange} />
                <Field label="Telephone" field="telephone" value={formData.telephone} onChange={handleChange} />
                <Field label="Current US Residence" field="address" value={formData.address} onChange={handleChange} type="textarea" />
                <Field label="Mailing Address" field="mailing_address" value={formData.mailing_address} onChange={handleChange} type="textarea" />
                <div>
                  <Label className="font-body text-xs text-muted-foreground mb-1 block">State</Label>
                  <Select value={formData.state} onValueChange={(v) => {
                    handleChange('state', v);
                    if (formData.county) handleChange('county', '');
                  }}>
                    <SelectTrigger className="h-9 text-sm" data-testid="state-select">
                      <SelectValue placeholder="Select state..." />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATE_OPTIONS.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="font-body text-xs text-muted-foreground mb-1 block">County</Label>
                  <CountyAutocomplete
                    value={formData.county}
                    onChange={(val) => handleChange('county', val)}
                    stateAbbr={formData.state}
                  />
                </div>
                {customFields.filter(f => f.group === 'contact').map(f => (
                  <Field key={f.id} label={f.field_name} field={f.field_key} value={customValues[f.field_key] || ''} onChange={(_, v) => handleCustomChange(f.field_key, v)} />
                ))}
              </div>
            </Section>

            {/* Immigration */}
            <Section title="Immigration" icon={Globe} defaultOpen={false}>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                <Field label="Country" field="country" value={formData.country} onChange={handleChange} />
                <Field label="Service Type" field="type_of_services" value={formData.type_of_services} onChange={handleChange} type="select" options={['Family Visa', 'Work Visa', 'Student Visa', 'Asylum', 'Green Card', 'Citizenship', 'DACA', 'Greencard Renewal', 'Other']} />
                <Field label="Date of Service" field="date_of_service" value={formData.date_of_service} onChange={handleChange} type="date" />
                <Field label="Date of Arrival" field="date_of_arrival" value={formData.date_of_arrival} onChange={handleChange} type="date" />
                <Field label="USCIS Receipt #" field="uscis_receipt" value={formData.uscis_receipt} onChange={handleChange} />
                <Field label="Receive By" field="receive_by" value={formData.receive_by} onChange={handleChange} />
                <Field label="USCIS Date" field="uscis_date" value={formData.uscis_date} onChange={handleChange} type="date" />
                <Field label="USCIS Decision" field="uscis_decision" value={formData.uscis_decision} onChange={handleChange} type="select" options={['Pending', 'Approved', 'Denied', 'RFE']} />
                <Field label="Biometrics Date" field="biometrics_date" value={formData.biometrics_date} onChange={handleChange} type="date" />
                <Field label="Department" field="department" value={formData.department} onChange={handleChange} />
                <Field label="Nonimmigrant Status" field="nonimmigrant_status" value={formData.nonimmigrant_status} onChange={handleChange} />
                <Field label="NI Status Exp. Date" field="nonimmigrant_status_exp_date" value={formData.nonimmigrant_status_exp_date} onChange={handleChange} type="date" />
                <Field label="NI Status Max Exp. Date" field="nonimmigrant_status_max_exp_date" value={formData.nonimmigrant_status_max_exp_date} onChange={handleChange} type="date" />
                {customFields.filter(f => f.group === 'immigration').map(f => (
                  <Field key={f.id} label={f.field_name} field={f.field_key} value={customValues[f.field_key] || ''} onChange={(_, v) => handleCustomChange(f.field_key, v)} />
                ))}
              </div>
            </Section>

            {/* Biographic */}
            <Section title="Biographic Information" icon={Fingerprint} defaultOpen={false}>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                <Field label="Ethnicity" field="ethnicity" value={formData.ethnicity} onChange={handleChange} type="select" options={['Hispanic or Latino', 'Not Hispanic or Latino']} />
                <Field label="Race" field="race" value={formData.race} onChange={handleChange} type="select" options={['White', 'Black or African American', 'Asian', 'American Indian or Alaska Native', 'Native Hawaiian or Pacific Islander', 'Other']} />
                <Field label="Height" field="height" value={formData.height} onChange={handleChange} />
                <Field label="Weight" field="weight" value={formData.weight} onChange={handleChange} />
                <Field label="Eye Color" field="eye_color" value={formData.eye_color} onChange={handleChange} type="select" options={['Black', 'Blue', 'Brown', 'Gray', 'Green', 'Hazel', 'Maroon', 'Pink', 'Other']} />
                <Field label="Hair Color" field="hair_color" value={formData.hair_color} onChange={handleChange} type="select" options={['Bald', 'Black', 'Blond', 'Brown', 'Gray', 'Red', 'Sandy', 'White', 'Other']} />
                {customFields.filter(f => f.group === 'biographic').map(f => (
                  <Field key={f.id} label={f.field_name} field={f.field_key} value={customValues[f.field_key] || ''} onChange={(_, v) => handleCustomChange(f.field_key, v)} />
                ))}
              </div>
            </Section>

            {/* Employment */}
            <Section title="Employment" icon={Briefcase} defaultOpen={false}>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                <Field label="Employer Name" field="employer_name" value={formData.employer_name} onChange={handleChange} />
                <Field label="Business Type" field="business_type" value={formData.business_type} onChange={handleChange} />
                <Field label="Job Title" field="job_title" value={formData.job_title} onChange={handleChange} />
                <Field label="Supervisor Name" field="supervisor_name" value={formData.supervisor_name} onChange={handleChange} />
                <Field label="Hours Per Week" field="hours_per_week" value={formData.hours_per_week} onChange={handleChange} />
                <Field label="Employer Email" field="employer_email" value={formData.employer_email} onChange={handleChange} />
                <Field label="Job Description" field="job_description" value={formData.job_description} onChange={handleChange} type="textarea" />
                {customFields.filter(f => f.group === 'employment').map(f => (
                  <Field key={f.id} label={f.field_name} field={f.field_key} value={customValues[f.field_key] || ''} onChange={(_, v) => handleCustomChange(f.field_key, v)} />
                ))}
              </div>
            </Section>

            {/* Submit */}
            <div className="flex justify-end gap-3 mt-6">
              <Button type="button" variant="outline" onClick={() => navigate('/cases')} className="font-body" data-testid="cancel-btn">Cancel</Button>
              <Button type="submit" disabled={loading} className="bg-primary text-white font-body" data-testid="submit-case-btn">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Create Case
              </Button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
